import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import './login.css';
import {
    AlertBannerType,
    ButtonFlavor,
    ButtonSize,
    ButtonType,
    ExAlertBanner,
    ExButton,
    ExLoader,
    LoaderSize,
} from '@boomi/exosphere';
import { tryGetErrorMessage } from '../inbox/utils';
import type { JwtToken } from '../types/global';
import { AUTH_BASE_URL } from '../constants/platform';

interface LoginProps {
    handleLoginSuccess: (jwt: JwtToken) => void;
}

const Login = ({ handleLoginSuccess }: LoginProps) => {
    const [error, setError] = useState<string | null>(null);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const jwtExpiryRef = useRef<number | null>(null);

    const auth = useMemo(
        () =>
            window.AuthUiClient?.Auth?.getInstance({
                baseUrl: AUTH_BASE_URL,
                origin: window.location.origin,
                system: 'platform',
                shouldSaveJwtToStorage: false,
            }),
        [],
    );

    const obtainAndSetJwt = useCallback(() => {
        setError(null);

        if (!auth) {
            setError('Authentication service not available');
            return;
        }

        auth.obtainJwt()
            .then((jwt) => {
                const jwtToken = jwt as JwtToken;
                handleLoginSuccess(jwtToken);

                jwtExpiryRef.current = jwtToken.exp * 1000;

                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                }

                intervalRef.current = setInterval(() => {
                    if (jwtExpiryRef.current && Date.now() > jwtExpiryRef.current) {
                        obtainAndSetJwt();
                    }
                }, 10_000);
            })
            .catch((err) => {
                const errorMessage = tryGetErrorMessage(err, 'Error obtaining JWT');
                console.error(errorMessage, err);
                setError(errorMessage);
            });
    }, [auth, handleLoginSuccess]);

    const handleLoginClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement> | CustomEvent<unknown>) => {
            event.preventDefault();
            obtainAndSetJwt();
        },
        [obtainAndSetJwt],
    );

    useEffect(() => {
        obtainAndSetJwt();
    }, [obtainAndSetJwt]);

    return error === null ? (
        <ExLoader size={LoaderSize.MEDIUM} />
    ) : (
        <div className="login-layout">
            <form className="login">
                <ExAlertBanner open={true} type={AlertBannerType.ERROR}>
                    {error}
                </ExAlertBanner>
                <ExButton
                    flavor={ButtonFlavor.BASE}
                    size={ButtonSize.LARGE}
                    type={ButtonType.PRIMARY}
                    onClick={handleLoginClick}
                >
                    Login
                </ExButton>
            </form>
            <div className="login-right"></div>
        </div>
    );
};

export default Login;
